/*
THREE.JS VIEWER

PROPS:

interactive: Allows for user interactivity (mouse and touch events)
autoAdjustSize: Adjusts the width and height of the canvas (number of pixels) to the DOM element size.
  Use this option if the size is established with the style parameters so that we avoid weird stretching
  behaviour
getSizeFromParent: Allows to adjust the canvas size to the element parent

This module views a three.js scene. It receives in props a scene manager with the
following shape
{
    getScene: function to retrieve the scene
    on: function to subscribe so that we receive a callback whenever the scene changes
    off: function to unsubscribe
    onMouseDown: function to call on mouse down
    onMouseMove: function to call on mouse move
    onMouseUp: function to call on mouse up
}

MOUSE INTERACTIVITY:

Scene interactivity cannot use DOM event handlers, as we are dealing with Webgl, therefore, events like onClick are not available.

Furthermore, DOM interactivity is based on 2D objects, and three.js interactivity is based on Three.js objects in a 3D scene.

In order to implement interactivity, the following things are required
y


a) Mouse/Touch handlers to respond to user events
b) Ray casting algorithm so that we know which part of the scene has been clicked
c) Re-emit algorithm using the supplied interface, passing a "referrer", or an object containing
   information on the clicked object, as supplied in the userData object within the 3D object which was clicked

AT MANAGER (PART OF THE APP MODIFYING THE MODEL)

a) When receiving a mouseDown event linked to a userData referrer, we will change the model to reflect that we are "dragging"
b) When moving the mouse, if we are dragging, we will use the referrer object to modify the position / angle properties of the
   objects which need to be moved (this is a decision taken by the manager)
c) After modifying the model, automatically onChange event will be triggered


BACK TO VIEWER

The viewer will call the function updateCanvas as a response to the onChange event. It will then call the getScene function
from the container, which in turn will call the three-selector

AT THREE-SELECTOR (PART WHICH SYNCS A THREE.JS SCENE WITH THE MODEL)

Once the onChange event is fired, the three-selector comes in place, by syncing the THREE.js scene with the hashedNode model.

The selector will change the matrices of the THREE objects according to the "abstract" positions in the app model.


FULL PATH

In summary, the full modification

Model mutation Path
THREE-VIEWER (1) --> CONTAINER (2) --> MANAGER (3) (APP MODEL MUTATION)

Paint Path
THREE-VIEWER (4) --> CONTAINER (5) --> SELECTOR --> (6) (THREE.JS SCENE RETURNED FOR PAINTING)


1. The viewer receives the mouse Event and performs ray casting to create a digested event with referrer
2. The container receives the call and passes it to the manager
3. The manager modifies the abstract app model (not thre three.js model), and that emits an onchange event automatically

4. In response to the onChange event, the viewer calls function updateCanvas
5. The viewer calls the container to get the scene
6. The container calls the selector, which sees that something changed in the model, and recalculates the three.js scene


For more info on simple-flow programming, pattern, please see info at https://github.com/eventbrite/simple_flow


*/

import React, { Component } from 'react';

import {
    Color,
    Matrix4,
    OrthographicCamera,
    Raycaster,
    Vector2,
    Vector3,
    WebGLRenderer,
} from 'three';

import debounce from 'lodash/debounce';
import {
    CAMERA_DEFAULT_POSITION_Z,
    CAMERA_DEFAULT_ZOOM,
    FIT_VIEW_FACTOR,
    FRUSTRUM_Z_MAX,
    FRUSTRUM_Z_MIN,
    PINCH_FACTOR,
} from './three-viewer-constants';

const BACKGROUND_COLOR = '#EEEEEE';
const MILLISECONDS_TO_WAIT_ON_RESIZE = 400;
const BOUNDING_EXTRA_MARGIN = 0.251;

export default class ThreeViewer extends Component {
    constructor(props) {
        super(props);
        const {
            autoAdjustSize,
            getSizeFromParent,
            interactive,
            sceneInterface,
        } = props;

        this.autoAdjustSize = autoAdjustSize;
        this.getSizeFromParent = getSizeFromParent;
        this.interactive = interactive;
        this.sceneInterface = sceneInterface;

        this.paintRequested = false;

        this._renderLoop = this._renderLoop.bind(this);

        this.onChange = this.onChange.bind(this);
        this.canvasRef = React.createRef();
        this.mouseDown = this.mouseDown.bind(this);
        this.mouseUp = this.mouseUp.bind(this);
        this.mouseMove = this.mouseMove.bind(this);
        this.touchStart = this.touchStart.bind(this);
        this.touchEnd = this.touchEnd.bind(this);
        this.touchMove = this.touchMove.bind(this);
        this.onResize = debounce(() => {
            this._onResize();
        }, MILLISECONDS_TO_WAIT_ON_RESIZE);
        this.onResize = this.onResize.bind(this);
        this.wheel = this.wheel.bind(this);
        this.view = {};
        this.startCamera = null;
        this.pinching = false;
        this.pinchStart = null;
        this.pinchCurrent = null;
        this.mousePos = null;
        this.closed = false;
    }

    updateCanvas() {
        this.paintRequested = true;
    }

    componentDidMount() {
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.onResize);
        }
        this.sceneInterface.on('change', this.onChange);
        // We request a canvas update
        this.updateCanvas();
        // We start the render cycle
        this._renderLoop();
    }

    componentWillUnmount() {
        this.sceneInterface.off('change', this.onChange);
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.onResize);
        }
        this.closed = true;
    }

    _onResize() {
        this.updateCanvas();
    }

    onChange() {
        this.updateCanvas();
    }

    _checkBoxList({ userData, matrix, pos }) {
        const boxList = userData.boxList;
        const boundingBox = userData.boundingBox;
        const p3d = new Vector3();
        const matrixInv = new Matrix4();

        matrixInv.getInverse(matrix);
        let index;

        if (boundingBox) {
            p3d.set(pos.x, pos.y, pos.z);
            p3d.applyMatrix4(matrixInv);
            if (
                p3d.x >= boundingBox.min.x &&
                p3d.y >= boundingBox.min.y &&
                p3d.x <= boundingBox.max.x &&
                p3d.y <= boundingBox.max.y
            ) {
                for (let i = 0; i < boxList.length; i++) {
                    index = 4 * i;
                    const u =
                        (p3d.x - boxList[index]) /
                        (boxList[index + 2] - boxList[index]);
                    const v =
                        (p3d.y - boxList[index + 1]) /
                        (boxList[index + 3] - boxList[index + 1]);
                    // if (u >= 0 && u <= 1 && v >= 0 && v <= 1) {
                    if (
                        u >= -BOUNDING_EXTRA_MARGIN &&
                        u <= 1 + BOUNDING_EXTRA_MARGIN &&
                        v >= -BOUNDING_EXTRA_MARGIN &&
                        v <= 1 + BOUNDING_EXTRA_MARGIN
                    ) {
                        return i;
                    }
                }
            }
        }
        return -1;
    }

    _getIntersectsRecursive(accum, pos, obj, fullMatrix) {
        const objFullMatrix = new Matrix4();

        objFullMatrix.multiplyMatrices(fullMatrix, obj.matrix);
        if (obj && obj.geometry && obj.userData.boxList) {
            const index = this._checkBoxList({
                matrix: objFullMatrix,
                pos,
                userData: obj.userData,
            });

            if (index !== -1) {
                accum.push({
                    index,
                    pos: {
                        x: pos.x,
                        y: pos.y,
                        z: pos.z,
                    },
                    userData: obj.userData,
                });
            }
        }
        const childrenNames = obj.children;

        childrenNames.forEach((child) => {
            this._getIntersectsRecursive(accum, pos, child, objFullMatrix);
        });
    }

    _getRay = (clientX, clientY) => {
        const margins = this.props.margins;
        const {
            bottom: marginBottom = 0,
            left: marginLeft = 0,
            right: marginRight = 0,
            top: marginTop = 0,
        } = margins;

        const effectiveClientX = clientX - marginLeft;
        const effectiveClientY = clientY - marginTop;
        const canvas = this.canvasRef.current;

        if (!canvas) {
            return null;
        }

        const rect = canvas.getBoundingClientRect();
        const pos = {
            x: effectiveClientX - rect.left,
            y: effectiveClientY - rect.top,
        };

        const camera = this.startCamera || this.view.camera;

        if (camera && canvas && this.interactive) {
            const containerWidth =
                canvas.clientWidth - marginLeft - marginRight;
            const containerHeight =
                canvas.clientHeight - marginTop - marginBottom;
            const mouseVector = new Vector2();
            const raycaster = new Raycaster();

            mouseVector.x = 2 * (pos.x / containerWidth) - 1;
            mouseVector.y = 1 - 2 * (pos.y / containerHeight);

            raycaster.setFromCamera(mouseVector, camera);
            return raycaster.ray;
        }
        return null;
    };

    getIntersects = (ray, scene) => {
        if (ray) {
            const intersects = [];
            const fullMatrix = new Matrix4();
            this._getIntersectsRecursive(
                intersects,
                ray.origin,
                scene,
                fullMatrix,
            );
            return intersects;
        }

        return [];
    };

    _multitouchFromTouch(touch) {
        return [{ clientX: 0, clientY: 0 }, touch];
    }

    touchStart(evt) {
        evt.preventDefault();
        const touchesToUse = [...evt.touches];

        if (touchesToUse.length > 1) {
            this.pinching = true;
            this.pinchStart = [...touchesToUse];
            this.pinchCurrent = [...touchesToUse];
        } else {
            this.pinching = false;
            const touch = touchesToUse[0];
            const mouseEvent = new MouseEvent('mousedown', {
                clientX: touch.clientX,
                clientY: touch.clientY,
            });

            mouseEvent.synthetic = true;
            mouseEvent.isTouch = true;

            const canvas = this.canvasRef.current;

            if (canvas) {
                canvas.dispatchEvent(mouseEvent);
            }
        }
    }

    _getLength(touch1, touch2) {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        const l = Math.sqrt(dx * dx + dy * dy);

        return l;
    }

    touchMove(e) {
        e.preventDefault();
        const touchesToUse = [...e.touches];

        if (this.pinching) {
            if (touchesToUse.length === this.pinchCurrent.length) {
                const l1 = this._getLength(touchesToUse[0], touchesToUse[1]);
                const l2 = this._getLength(
                    this.pinchCurrent[0],
                    this.pinchCurrent[1],
                );
                const delta = (l2 - l1) * PINCH_FACTOR;

                const { onWheel } = this.sceneInterface;
                const touchMiddle = {
                    x:
                        (this.pinchStart[0].clientX +
                            this.pinchStart[1].clientX) *
                        0.5,
                    y:
                        (this.pinchStart[0].clientY +
                            this.pinchStart[1].clientY) *
                        0.5,
                };
                const ray = this._getRay(touchMiddle.x, touchMiddle.y);
                const pos = ray.origin;

                this.pinchCurrent = touchesToUse;
                if (onWheel) {
                    onWheel({ delta, isTouch: true, pos });
                }
            }
        } else {
            const touch = e.touches[0];
            const mouseEvent = new MouseEvent('mousemove', {
                clientX: touch.clientX,
                clientY: touch.clientY,
            });

            mouseEvent.synthetic = true;
            mouseEvent.isTouch = true;
            const canvas = this.canvasRef.current;

            if (canvas) {
                canvas.dispatchEvent(mouseEvent);
            }
        }
    }

    touchEnd(e) {
        e.preventDefault();
        const mouseEvent = new MouseEvent('mouseup', {});

        mouseEvent.synthetic = true;
        const canvas = this.canvasRef.current;

        if (canvas) {
            canvas.dispatchEvent(mouseEvent);
        }
    }

    mouseDown(evt) {
        if (!evt.synthetic) {
            evt.preventDefault();
        }
        const { clientX, clientY, isTouch, altKey, ctrlKey, shiftKey } = evt;

        const { getScene, onMouseDown } = this.sceneInterface || {};

        if (!getScene) {
            return;
        }

        if (!this.view.camera) {
            return;
        }

        const canvas = this.canvasRef.current;

        if (!canvas) {
            return;
        }

        this.startCamera = this.view.camera.clone();
        const camera = this.view.camera;

        const scene = getScene({ camera });

        const ray = this._getRay(clientX, clientY);
        const rayOrigin = ray.origin;
        const pos = {
            x: rayOrigin.x,
            y: rayOrigin.y,
            z: rayOrigin.z,
        };
        const intersects = this.getIntersects(ray, scene);

        if (onMouseDown) {
            onMouseDown({
                intersects,
                isTouch,
                pos,
                keys: {
                    alt: altKey,
                    ctrl: ctrlKey,
                    shift: shiftKey,
                },
            });
        }
    }

    mouseUp(evt) {
        if (!evt.synthetic) {
            evt.preventDefault();
        }

        this.startCamera = null;
        const { onMouseUp } = this.sceneInterface;

        if (onMouseUp) {
            onMouseUp();
        }
    }

    mouseMove(evt) {
        if (!evt.synthetic) {
            evt.preventDefault();
        }
        const { clientX, clientY, isTouch, altKey, ctrlKey, shiftKey } = evt;

        const { getScene, onMouseMove } = this.sceneInterface || {};

        if (!this.view || !this.view.camera || !getScene || !onMouseMove) {
            return;
        }
        const canvas = this.canvasRef.current;

        if (!canvas) {
            return;
        }

        const camera = this.view.camera;

        const scene = getScene({ camera });

        const ray = this._getRay(clientX, clientY);
        const rayOrigin = ray.origin;
        const pos = {
            x: rayOrigin.x,
            y: rayOrigin.y,
            z: rayOrigin.z,
        };
        const intersects = this.getIntersects(ray, scene);

        onMouseMove({
            intersects,
            isTouch,
            pos,
            keys: {
                alt: altKey,
                ctrl: ctrlKey,
                shift: shiftKey,
            },
        });
    }

    wheel(evt) {
        evt.preventDefault();
        const { clientX, clientY, deltaY, isTouch } = evt;
        const { onWheel } = this.sceneInterface;
        const canvas = this.canvasRef.current;

        if (!canvas) {
            return;
        }
        if (onWheel) {
            const ray = this._getRay(clientX, clientY);

            if (ray) {
                const rayOrigin = ray.origin;
                const pos = {
                    x: rayOrigin.x,
                    y: rayOrigin.y,
                    z: rayOrigin.z,
                };

                onWheel({
                    delta: deltaY,
                    isTouch,
                    pos,
                });
            }
        }
    }

    _updateCamera({
        canvas,
        width,
        height,
        marginLeft,
        marginRight,
        marginTop,
        marginBottom,
    }) {
        const effectiveWidth = width - marginLeft - marginRight;
        const effectiveHeight = height - marginTop - marginBottom;
        const camera = new OrthographicCamera(
            effectiveWidth / -2,
            effectiveWidth / 2,
            effectiveHeight / -2,
            effectiveHeight / 2,
            FRUSTRUM_Z_MIN,
            FRUSTRUM_Z_MAX,
        );

        camera.position.z = CAMERA_DEFAULT_POSITION_Z;
        camera.zoom = CAMERA_DEFAULT_ZOOM;
        camera.updateProjectionMatrix();

        let renderer = this.view.renderer;

        if (!renderer) {
            renderer = new WebGLRenderer({ antialias: true, canvas });
            renderer.setClearColor(new Color(BACKGROUND_COLOR));
        }
        renderer.setSize(width, height);
        renderer.setViewport(
            marginLeft,
            marginTop,
            effectiveWidth,
            effectiveHeight,
        );
        renderer.setScissor(
            marginLeft,
            marginTop,
            effectiveWidth,
            effectiveHeight,
        );
        this.view.renderer = renderer;
        this.view.camera = camera;

        const sceneInterface = this.sceneInterface;

        if (sceneInterface) {
            const getViewport = sceneInterface.getViewport;
            const setViewport = sceneInterface.setViewport;

            if (getViewport) {
                const viewport = getViewport();
                const { height: viewportHeight, width: viewportWidth } =
                    viewport || {};

                if (
                    viewportWidth !== effectiveWidth ||
                    viewportHeight !== effectiveHeight
                ) {
                    if (setViewport) {
                        setViewport({
                            height: effectiveHeight,
                            width: effectiveWidth,
                        });
                    }
                }
            }
        }
    }

    // Continuous render
    //eslint-disable-next-line
    _renderLoop() {
        const { autoAdjustSize, getSizeFromParent, margins } = this.props;
        const {
            bottom: marginBottom = 0,
            left: marginLeft = 0,
            right: marginRight = 0,
            top: marginTop = 0,
        } = margins;
        const sceneInterface = this.sceneInterface;
        const getScene = sceneInterface.getScene;
        const getViewBox = sceneInterface.getViewBox;
        const getIsAnimating = sceneInterface.getIsAnimating;

        if (!this.closed) {
            requestAnimationFrame(this._renderLoop);
        }
        const isAnimating = getIsAnimating();
        // If no repaint is requested, we just do not paint
        if (!this.paintRequested && !isAnimating) {
            return;
        }
        // We disable paintRequested
        this.paintRequested = false;

        const canvas = this.canvasRef.current;
        let offsetWidth;
        let offsetHeight;

        if (!canvas) {
            return;
        }

        if (getSizeFromParent) {
            offsetWidth = canvas.parentNode.clientWidth;
            offsetHeight = canvas.parentNode.clientHeight;
        } else {
            offsetWidth = canvas.offsetWidth;
            offsetHeight = canvas.offsetHeight;
        }

        // We retrieve the actual size of the canvas, and we make sure
        // that the amount of pixels in the canvas is the same as the
        // amount of pixels in the DOM element
        if (
            !this.view.renderer ||
            (autoAdjustSize &&
                (offsetWidth !== canvas.width ||
                    offsetHeight !== canvas.height)) ||
            marginBottom !== canvas.marginBottom ||
            marginTop !== canvas.marginTop ||
            marginLeft !== canvas.marginLeft ||
            marginRight !== canvas.marginRight
        ) {
            canvas.width = offsetWidth;
            canvas.height = offsetHeight;
            canvas.marginTop = marginTop;
            canvas.marginBottom = marginBottom;
            canvas.marginLeft = marginLeft;
            canvas.marginRight = marginRight;
            this._updateCamera({
                canvas,
                height: canvas.height,
                marginBottom,
                marginLeft,
                marginRight,
                marginTop,
                width: canvas.width,
            });
        }

        if (!canvas.listenersAdded && !this.lockCanvas) {
            canvas.addEventListener('mousemove', this.mouseMove);
            canvas.addEventListener('mousedown', this.mouseDown);
            canvas.addEventListener('mouseup', this.mouseUp);
            canvas.addEventListener('touchstart', this.touchStart);
            canvas.addEventListener('touchmove', this.touchMove);
            canvas.addEventListener('touchend', this.touchEnd);
            canvas.addEventListener('wheel', this.wheel);
        } else if (this.lockCanvas) {
            canvas.removeEventListener('mousemove', this.mouseMove);
            canvas.removeEventListener('mousedown', this.mouseDown);
            canvas.removeEventListener('mouseup', this.mouseUp);
            canvas.removeEventListener('touchstart', this.touchStart);
            canvas.removeEventListener('touchmove', this.touchMove);
            canvas.removeEventListener('touchend', this.touchEnd);
            canvas.removeEventListener('wheel', this.wheel);
            canvas.listenersAdded = false;
        }

        if (getScene) {
            const renderer = this.view.renderer;
            const camera = this.view.camera;

            if (renderer) {
                if (getViewBox) {
                    const viewBox = getViewBox();

                    if (viewBox) {
                        const effectiveWidth =
                            offsetWidth - marginLeft - marginRight;
                        const effectiveHeight =
                            offsetHeight - marginTop - marginBottom;

                        camera.zoom =
                            FIT_VIEW_FACTOR *
                            Math.min(
                                effectiveWidth /
                                    (viewBox.max.x - viewBox.min.x),
                                effectiveHeight /
                                    (viewBox.max.y - viewBox.min.y),
                            );
                        const center = {
                            x: (viewBox.max.x + viewBox.min.x) * 0.5,
                            y: (viewBox.max.y + viewBox.min.y) * 0.5,
                            z: 0,
                        };

                        camera.position.set(
                            center.x,
                            center.y,
                            CAMERA_DEFAULT_POSITION_Z,
                        );
                        camera.updateProjectionMatrix();
                    }
                }

                const scene = getScene({ camera });
                const t1 = Date.now();

                renderer.render(scene, camera);
                const t2 = Date.now();
                const totalTime = t2 - t1;

                if (totalTime > 20) {
                    // console.log('Three.js render time ', totalTime);
                }
            }
        }
    }

    render() {
        //console.log('three-viewer rendering');

        return <canvas ref={this.canvasRef} />;
    }
}
